import React, { Fragment, useState, useEffect } from "react";
import { Transition } from "@headlessui/react";
import { useNavigate } from "react-router-dom";
import {
    Users,
    Play,
    X,
    MessageCircle,
    Briefcase,
    Heart,
    BookOpen,
} from "lucide-react";

// Public-facing data. Swap image URLs / video IDs with real ones whenever ready.
const FOUNDERS = [
    {
        id: "kevin",
        name: "Kevin",
        role: "CO-FOUNDER",
        photo: "/kevin2.png",
        videoUrl: "https://res.cloudinary.com/nyolacloudinary/video/upload/v1778589506/kevin_sq_vid_001_pm5azx.mp4",
        watchLabel: "Watch Kevin's story",
        quote:
            "We built Wefayo because everyone deserves a safe space to ask questions, share stories, and find people who truly understand.",
        tags: [
            { label: "Product", icon: MessageCircle },
            { label: "Impact", icon: Users },
        ],
    },
    {
        id: "henry",
        name: "Henry",
        role: "CO-FOUNDER",
        photo: "/henrypic2.png",
        videoUrl: "https://res.cloudinary.com/nyolacloudinary/video/upload/v1778500018/henry_square_001_uaaq4f.mp4",
        watchLabel: "Watch Henry's story",
        quote:
            "After watching a close friend struggle to find support communities for a rare condition, I knew technology could bridge that gap.",
        tags: [
            { label: "Community", icon: Heart },
            { label: "Vision", icon: BookOpen },
        ],
    },
];

const QUICK_LINKS = [
    { label: "Communities", icon: Users, path: "/" },
    { label: "Chat", icon: MessageCircle, path: "/wefayo-ai" },
    { label: "Jobs", icon: Briefcase, path: "/jobs" },
    { label: "Resources", icon: Heart, path: "/blue-envelope-program" },
];

// Replace with your real product walkthrough URL.
const PRODUCT_VIDEO_URL = "https://res.cloudinary.com/nyolacloudinary/video/upload/v1778250735/demowefayovideo_ufdt74.mp4";

const TABS = {
    FOUNDERS: "founders",
    ACTION: "action",
};

// Direct video files can be rendered with a native <video> element so the
// frame fills the container exactly like an <img object-cover>. Embed URLs
// (YouTube, Vimeo, etc.) must use an <iframe> instead.
const isDirectVideoFile = (url) =>
    typeof url === "string" && /\.(mp4|webm|ogg|mov|m4v)(\?|$)/i.test(url);

const WelcomeIntroModal = ({ isOpen, onClose }) => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(TABS.FOUNDERS);
    const [playingFounderId, setPlayingFounderId] = useState(null);

    // Close on ESC + lock body scroll while open.
    useEffect(() => {
        if (!isOpen) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = previousOverflow;
        };
    }, [isOpen, onClose]);

    const handleExplore = () => {
        onClose();
        navigate("/");
    };

    const handleSignUp = () => {
        onClose();
        navigate("/register");
    };

    const handleQuickLink = (path) => {
        onClose();
        navigate(path);
    };

    const tabBaseClass =
        "inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors";
    const tabActiveClass = "bg-white text-purple-900 shadow-sm";
    const tabInactiveClass =
        "bg-white/15 text-white border border-white/30 hover:bg-white/25";

    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <div
                className="fixed inset-0"
                style={{ zIndex: 100 }}
                role="dialog"
                aria-modal="true"
                aria-label="Welcome to Wefayo"
            >
                {/* Backdrop — clicking here closes the popup */}
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div
                        className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
                        onClick={onClose}
                    />
                </Transition.Child>

                {/* Centered panel layer */}
                <div className="absolute inset-0 overflow-y-auto">
                    <div
                        className="flex min-h-full items-center justify-center p-3 sm:p-6"
                        onClick={onClose}
                    >
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-6 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:scale-95"
                        >
                            <div
                                className="relative w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl"
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                            >
                                {/* ── Purple header ────────────────────────────── */}
                                <div
                                    className="relative pl-5 pr-12 sm:pl-8 sm:pr-14 pt-6 pb-7 text-white"
                                    style={{
                                        background:
                                            "linear-gradient(135deg, #581c87 0%, #6b21a8 60%, #7e22ce 100%)",
                                    }}
                                >
                                    {/* Close button */}
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        aria-label="Close welcome popup"
                                        className="absolute top-3 right-3 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                                    >
                                        <X className="h-4 w-4 text-white" />
                                    </button>

                                    {/* Top line: avatar + welcome label */}
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="rounded-full bg-white/30  flex items-center justify-center overflow-hidden">
                                            <img
                                                src="/logo.png"
                                                alt="Wefayo"
                                                className="h-7 w-7 object-contain"
                                                onError={(e) => {
                                                    e.currentTarget.style.display = "none";
                                                }}
                                            />
                                        </div>
                                        <p className="text-[11px] sm:text-xs font-semibold tracking-[0.18em] uppercase text-white/95">
                                            Welcome to our community
                                        </p>
                                    </div>

                                    {/* Title — single line across all breakpoints */}
                                    <h2
                                        className="font-extrabold leading-tight text-white whitespace-nowrap overflow-hidden text-ellipsis text-[13px] sm:text-[16px] md:text-[20px] lg:text-[22px]"
                                        style={{ fontFamily: '"Chakra Petch", sans-serif' }}
                                    >
                                        Building a world where no one faces health challenges alone.
                                    </h2>

                                    {/* Tabs */}
                                    <div className="mt-5 flex flex-wrap gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setActiveTab(TABS.FOUNDERS)}
                                            className={`${tabBaseClass} ${
                                                activeTab === TABS.FOUNDERS
                                                    ? tabActiveClass
                                                    : tabInactiveClass
                                            }`}
                                        >
                                            <Users className="h-4 w-4" />
                                            Our Founders
                                        </button>
                                    </div>
                                </div>

                                {/* ── Body ─────────────────────────────────────── */}
                                <div className="max-h-[55vh] overflow-y-auto px-5 sm:px-8 py-6">
                                    {activeTab === TABS.FOUNDERS ? (
                                        <FoundersTab
                                            playingFounderId={playingFounderId}
                                            setPlayingFounderId={setPlayingFounderId}
                                        />
                                    ) : (
                                        <ActionTab onQuickLink={handleQuickLink} />
                                    )}
                                </div>

                                {/* ── Footer CTA ──────────────────────────────── */}
                                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-gray-100 px-5 sm:px-8 py-4 bg-white">
                                    <p className="text-sm text-gray-600 text-center sm:text-left">
                                        Join 20+ health &amp; wellness communities today
                                    </p>
                                    <div className="flex flex-wrap items-center justify-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setActiveTab(TABS.ACTION)}
                                            className="inline-flex items-center justify-center gap-1.5 rounded-full border border-purple-200 bg-white hover:bg-purple-50 text-purple-900 font-semibold px-4 py-2 text-sm transition-colors"
                                        >
                                            <Play className="h-4 w-4" />
                                            Wefayo Demo
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleExplore}
                                            className="inline-flex items-center justify-center rounded-full bg-purple-900 hover:bg-purple-800 text-white font-semibold px-5 py-2 text-sm transition-colors shadow-md"
                                        >
                                            Explore Wefayo
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleSignUp}
                                            className="inline-flex items-center justify-center rounded-full bg-purple-900 hover:bg-purple-800 text-white font-semibold px-5 py-2 text-sm transition-colors shadow-md"
                                        >
                                            Sign Up
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </Transition.Child>
                    </div>
                </div>
            </div>
        </Transition.Root>
    );
};

const FoundersTab = ({ playingFounderId, setPlayingFounderId }) => (
    <div>
        <p className="text-sm text-gray-600 mb-5">
            Wefayo was born from a shared belief that community is the most powerful
            medicine. Meet the two people who turned that belief into a platform.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {FOUNDERS.map((founder) => (
                <FounderCard
                    key={founder.id}
                    founder={founder}
                    isPlaying={playingFounderId === founder.id}
                    onPlay={() => setPlayingFounderId(founder.id)}
                    onStop={() => setPlayingFounderId(null)}
                />
            ))}
        </div>
    </div>
);

const FounderCard = ({ founder, isPlaying, onPlay, onStop }) => {
    return (
        <div className="rounded-xl overflow-hidden border border-gray-200 bg-white">
            {/* Image / video area */}
            <div className="relative aspect-[3/3] bg-gradient-to-br from-purple-100 to-purple-200">
                {/* Poster (always rendered so the click target is never detached) */}
                <img
                    src={founder.photo}
                    alt={founder.name}
                    className="absolute inset-0 h-full w-full object-cover"
                    onError={(e) => {
                        e.currentTarget.style.display = "none";
                    }}
                />
                <button
                    type="button"
                    onClick={onPlay}
                    aria-label={founder.watchLabel}
                    className={`absolute inset-0 flex items-center justify-center group ${
                        isPlaying ? "pointer-events-none opacity-0" : ""
                    }`}
                >
                    <span className="h-12 w-12 rounded-full bg-purple-600/90 hover:bg-purple-600 flex items-center justify-center shadow-lg transition-colors">
                        <Play className="h-5 w-5 text-white fill-white ml-0.5" />
                    </span>
                </button>
                <span
                    className={`absolute bottom-2 left-2 rounded-md bg-gray-900/75 text-white text-[11px] font-medium px-2 py-1 ${
                        isPlaying ? "hidden" : ""
                    }`}
                >
                    {founder.watchLabel}
                </span>

                {/* Video overlay — rendered on top of the poster, never replaces it */}
                {isPlaying && (
                    <>
                        {isDirectVideoFile(founder.videoUrl) ? (
                            <video
                                key={founder.videoUrl}
                                src={founder.videoUrl}
                                className="absolute inset-0 h-full w-full object-cover bg-black"
                                autoPlay
                                controls
                                playsInline
                            />
                        ) : (
                            <iframe
                                title={`${founder.name}'s story`}
                                src={`${founder.videoUrl}?autoplay=1`}
                                className="absolute inset-0 h-full w-full"
                                frameBorder="0"
                                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                        )}
                        <button
                            type="button"
                            onClick={onStop}
                            className="absolute top-2 right-2 z-10 inline-flex h-7 w-7 items-center justify-center rounded-full bg-gray-900/70 hover:bg-gray-900 text-white"
                            aria-label="Close video"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    </>
                )}
            </div>

            {/* Text section */}
            <div className="p-3.5">
                <div className="flex items-baseline gap-2 mb-1.5">
                    <h3 className="text-base font-bold text-gray-900">
                        {founder.name}
                    </h3>
                    <span className="text-[10px] font-bold tracking-widest text-purple-700">
                        {founder.role}
                    </span>
                </div>
                <p className="text-sm text-gray-700 leading-snug">
                    &ldquo;{founder.quote}&rdquo;
                </p>
                <div className="flex flex-wrap gap-1.5 mt-3">
                    {founder.tags.map((tag) => {
                        const Icon = tag.icon;
                        return (
                            <span
                                key={tag.label}
                                className="inline-flex items-center gap-1 rounded-full bg-purple-50 text-purple-700 text-[11px] font-medium px-2.5 py-1 border border-purple-100"
                            >
                                <Icon className="h-3 w-3" />
                                {tag.label}
                            </span>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

const ActionTab = ({ onQuickLink }) => (
    <div>
        <p className="text-sm text-gray-600 mb-4">
            See how Wefayo connects communities, provides resources, and empowers
            people facing health challenges.
        </p>

        <div className="relative w-full overflow-hidden rounded-xl bg-black aspect-video">
            <iframe
                title="Wefayo in action"
                src={PRODUCT_VIDEO_URL}
                className="absolute inset-0 h-full w-full"
                frameBorder="0"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
            />
        </div>

        {/* <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {QUICK_LINKS.map((link) => {
                const Icon = link.icon;
                return (
                    <button
                        key={link.label}
                        type="button"
                        onClick={() => onQuickLink(link.path)}
                        className="flex flex-col items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white hover:border-purple-300 hover:bg-purple-50 transition-colors py-4"
                    >
                        <Icon className="h-5 w-5 text-purple-600" />
                        <span className="text-sm font-semibold text-gray-800">
                            {link.label}
                        </span>
                    </button>
                );
            })}
        </div> */}
    </div>
);

export default WelcomeIntroModal;
